<template>
  <div class="task-title">
    <template v-if="emoji">
      <span class="emoji">{{ emoji }}</span>
    </template>
    <template v-else>
      <TaskIcon class="task-icon" />
    </template>
    <span class="name">{{ title || "Loading..." }}</span>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, shallowRef, toRefs, watch } from "vue";

import { FullTask, ReleasableRef } from "@ctrl/controller-rpc";

import TaskIcon from "@/components/icons/TaskIcon.vue";
import * as inject from "@/components/inject";
import { useEmoji } from "@/composables/useEmoji";
import { useReleaser } from "@/ref";

export default defineComponent({
  name: "TaskTitle",
  components: {
    TaskIcon,
  },
  props: {
    taskId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const { taskId } = toRefs(props);
    const controller = inject.controller();
    const { title, emoji, updateText } = useEmoji();

    const taskRef = shallowRef<ReleasableRef<FullTask | null> | null>(null);
    // Computed property to access the actual task value
    const task = computed<FullTask | null>(() => taskRef.value?.value ?? null);
    watch(
      task,
      (taskVal) => {
        if (!taskVal) return;
        updateText(taskVal.title || "Untitled");
      },
      { immediate: true }
    );

    const releaser = useReleaser();
    taskRef.value = await releaser.add(controller.task.get(taskId.value));

    return {
      title,
      emoji,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/emoji.scss";

.task-title {
  display: flex;
  align-items: center;
  gap: 0px;
  width: 100%;
}

.task-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.selected {
    @include themed() {
      color: t($tertiary-50);
    }
  }
}
</style>
